import React from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { AdminProvider, useAdmin } from './context/AdminContext';
import { CartProvider } from './context/CartContext';
import { LanguageProvider } from './context/LanguageContext';
import { api } from './lib/api';

// Pages
import { LandingPage } from './pages/LandingPage';
import { LoginPage, RegisterPage } from './pages/AuthPages';
import { VerifyEmailPage } from './pages/VerifyEmailPage';
import { LegalPage } from './pages/LegalPage';
import { DashboardLayout, DashboardOverview } from './pages/Dashboard';
import { LiveOrdersPage } from './pages/LiveOrdersPage';
import { OrdersHistoryPage } from './pages/OrdersHistoryPage';
import { CustomersPage } from './pages/CustomersPage';
import { MenuPage } from './pages/MenuPage';
import { ModifiersPage } from './pages/ModifiersPage';
import { ReservationsPage } from './pages/ReservationsPage';
import { AIInsightsPage } from './pages/AIInsightsPage';
import { SettingsPage } from './pages/SettingsPage';
import { SubscriptionPage } from './pages/SubscriptionPage';
import { DeliveryZonesPage } from './pages/DeliveryZonesPage';
import { PrinterSettingsPage } from './pages/PrinterSettingsPage';
import PrintStationPage from './pages/PrintStationPage';
import { MarketingPage } from './pages/MarketingPage';
import { CustomerWebshop, OrderSuccessPage } from './pages/CustomerWebshop';
import { BlogPage } from './pages/BlogPage';
import { BlogPostPage } from './pages/BlogPostPage';

// Admin Pages
import { AdminSeoPage } from './pages/AdminSeoPage';
import { AdminBlogPage } from './pages/AdminBlogPage';
import { AdminLegalPage } from './pages/AdminLegalPage';
import { AdminOnboardingPage } from './pages/AdminOnboardingPage';
import { AdminLoginPage } from './pages/AdminLoginPage';
import { 
  AdminLayout, 
  AdminDashboard, 
  AdminRestaurantsPage, 
  AdminOrdersPage, 
  AdminSubscriptionsPage,
  AdminDomainsPage,
  AdminInvoicesPage,
  AdminSettingsPage 
} from './pages/AdminPages';

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
        <div className="spinner"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Admin Protected Route wrapper
const AdminProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAdmin();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }
  
  return children;
};

// Dashboard routes wrapper
const DashboardRoutes = () => {
  return (
    <DashboardLayout>
      <Routes>
        <Route index element={<DashboardOverview />} />
        <Route path="orders" element={<Navigate to="/dashboard/live-orders" replace />} />
        <Route path="live-orders" element={<LiveOrdersPage />} />
        <Route path="orders-history" element={<OrdersHistoryPage />} />
        <Route path="customers" element={<CustomersPage />} />
        <Route path="menu" element={<MenuPage />} />
        <Route path="modifiers" element={<ModifiersPage />} />
        <Route path="reservations" element={<ReservationsPage />} />
        <Route path="delivery-zones" element={<DeliveryZonesPage />} />
        <Route path="printer" element={<PrinterSettingsPage />} />
        <Route path="marketing" element={<MarketingPage />} />
        <Route path="subscription" element={<SubscriptionPage />} />
        <Route path="ai-insights" element={<AIInsightsPage />} />
        <Route path="settings" element={<SettingsPage />} />
      </Routes>
    </DashboardLayout>
  );
};

// Admin routes wrapper
const AdminRoutes = () => {
  return (
    <AdminProtectedRoute>
      <Routes>
        <Route element={<AdminLayout />}>
          <Route index element={<AdminDashboard />} />
          <Route path="restaurants" element={<AdminRestaurantsPage />} />
          <Route path="subscriptions" element={<AdminSubscriptionsPage />} />
          <Route path="invoices" element={<AdminInvoicesPage />} />
          <Route path="domains" element={<AdminDomainsPage />} />
          <Route path="seo" element={<AdminSeoPage />} />
          <Route path="blog" element={<AdminBlogPage />} />
          <Route path="legal" element={<AdminLegalPage />} />
          <Route path="onboarding" element={<AdminOnboardingPage />} />
          <Route path="orders" element={<AdminOrdersPage />} />
          <Route path="settings" element={<AdminSettingsPage />} />
        </Route>
      </Routes>
    </AdminProtectedRoute>
  );
};

// ===== Custom domain (white-label webshop) support =====
const PLATFORM_HOSTS = ['food-flows.com', 'localhost', '127.0.0.1'];
const isPlatformHost = (host) =>
  PLATFORM_HOSTS.some((h) => host === h || host === `www.${h}`) ||
  host.endsWith('.food-flows.com') ||
  host.endsWith('.emergentagent.com') ||
  host.endsWith('.emergent.host');

const CustomDomainShop = () => {
  const [state, setState] = React.useState({ loading: true, slug: null });

  React.useEffect(() => {
    api.get('/public/domain-lookup', { params: { domain: window.location.hostname } })
      .then((res) => setState({ loading: false, slug: res.data.found ? res.data.slug : null }))
      .catch(() => setState({ loading: false, slug: null }));
  }, []);

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!state.slug) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA] px-4" data-testid="domain-not-activated">
        <div className="max-w-md text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-3">Dit domein is nog niet geactiveerd</h1>
          <p className="text-gray-600 mb-6">
            De webshop op dit domein is nog niet actief. Ben je de eigenaar? Controleer je domeininstellingen in het restaurant dashboard.
          </p>
          <a href="https://food-flows.com" className="text-emerald-600 underline">Powered by FoodFlows</a>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/" element={<CustomerWebshop slugOverride={state.slug} />} />
      <Route path="/order-success" element={<OrderSuccessPage />} />
      <Route path="/shop/:slug" element={<CustomerWebshop />} />
      <Route path="/shop/:slug/order-success" element={<OrderSuccessPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  const isCustomDomain = !isPlatformHost(window.location.hostname);

  if (isCustomDomain) {
    return (
      <LanguageProvider defaultLanguage="nl">
        <CartProvider>
          <BrowserRouter>
            <CustomDomainShop />
          </BrowserRouter>
          <Toaster position="top-right" richColors />
        </CartProvider>
      </LanguageProvider>
    );
  }

  return (
    <AuthProvider>
      <AdminProvider>
        <LanguageProvider defaultLanguage="nl">
          <CartProvider>
            <BrowserRouter>
              <Routes>
                {/* Public routes */}
                <Route path="/" element={<LandingPage />} />
                <Route path="/blog" element={<BlogPage />} />
                <Route path="/blog/:lang" element={<BlogPage />} />
                <Route path="/blog/:lang/:slug" element={<BlogPostPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/verifieer-email" element={<VerifyEmailPage />} />
                <Route path="/voorwaarden" element={<LegalPage docType="terms" />} />
                <Route path="/verwerkersovereenkomst" element={<LegalPage docType="dpa" />} />
                <Route path="/privacybeleid" element={<LegalPage docType="privacy" />} />
                
                {/* Customer webshop routes */}
                <Route path="/shop/:slug" element={<CustomerWebshop />} />
                <Route path="/order-success" element={<OrderSuccessPage />} />
                <Route path="/shop/:slug/order-success" element={<OrderSuccessPage />} />

                {/* Public Print Station (browser-based auto-print) */}
                <Route path="/print-station/:apiKey" element={<PrintStationPage />} />
                
                {/* Admin routes */}
                <Route path="/admin/login" element={<AdminLoginPage />} />
                <Route path="/admin/dashboard/*" element={<AdminRoutes />} />
                <Route path="/admin" element={<Navigate to="/admin/login" replace />} />
                
                {/* Protected dashboard routes */}
                <Route 
                  path="/dashboard/*" 
                  element={
                    <ProtectedRoute>
                      <DashboardRoutes />
                    </ProtectedRoute>
                  } 
                />
                
                {/* Fallback */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </BrowserRouter>
            <Toaster position="top-right" richColors />
          </CartProvider>
        </LanguageProvider>
      </AdminProvider>
    </AuthProvider>
  );
}

export default App;
